import {
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { AppModal } from "global/components/AppModal";
import { useContext, useState } from "react";
import notifiers from "global/constants/NotificationConstants";
import {
  openErrorNotification,
  isTruthy,
  openSuccessNotification,
  handleErrorMessage,
} from "helpers/methods";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import { addPaymentMethod } from "../PlanService";

interface CustomProps {
  isDialogOpen: boolean;
  setDialogOpen: Function;
  doesNewPaymentMethodAdded: boolean;
  setDoesNewPaymentMethodAdded: Function;
  fetchPaymentMethods: any;
}

export const cardStyle = {
  style: {
    base: {
      color: "#00000",
      fontFamily: "'Montserrat', sans-serif",
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": {
        color: "primary.main",
      },
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a",
    },
  },
};

const AddPaymentMethodDialog = (props: CustomProps) => {
  const [defaultCardStatus, setDefaultCardStatus] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const stripe = useStripe();
  const elements = useElements();
  const [isCardComplete, setIsCardComplete] = useState<boolean>(false);

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      if (stripe !== null && elements !== null) {
        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: "card",
          card: elements.getElement(CardElement)!,
        });
        if (error) {
          openErrorNotification(
            isTruthy(error) ? error.message : notifiers.GENERIC_ERROR
          );
          setIsLoading(false);
        } else {
          await addCardHandler(paymentMethod?.id);
        }
        await props.fetchPaymentMethods();
      } else {
        setIsLoading(false);
      }
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const addCardHandler = async (id: any) => {
    try {
      await addPaymentMethod(id, defaultCardStatus);
      openSuccessNotification("Payment method added successfully");
      props.setDoesNewPaymentMethodAdded(true);
      handlePaymentMethodClose();
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const handlePaymentMethodClose = () => {
    setDefaultCardStatus(false);
    setIsCardComplete(false);
    setIsLoading(false);
    props.setDialogOpen(false);
    props.setDoesNewPaymentMethodAdded(false);
  };

  const getBody = () => {
    return (
      <Stack direction="column" pt={1}>
        <Stack>
          <Box
            sx={{
              border: "#47556940 solid 1px",
              borderRadius: "9px",
              padding: "12px",
              backgroundColor: "#fff",
            }}
          >
            <CardElement
              options={cardStyle}
              id="card_number"
              onChange={(e) => setIsCardComplete(e.complete)}
            />
          </Box>
        </Stack>
        <Stack>
          <FormControlLabel
            value="male"
            control={
              <Checkbox
                onChange={(e: any) => {
                  setDefaultCardStatus(e.target.checked);
                }}
                checked={defaultCardStatus}
                sx={{
                  paddingLeft: "12px",
                  paddingRight: "4px",
                  "&.Mui-checked": {
                    color: "#BB0070",
                  },
                }}
                id="set_default_card_checkbox"
              />
            }
            label="Set as default"
          />
        </Stack>
        <Stack pt={1}>
          <Typography variant="body2">
            By adding your card here and marking it as the default, you
            authorize the system to automatically charge this default card for
            any future payments.
          </Typography>
        </Stack>
      </Stack>
    );
  };

  return (
    <>
      <AppModal
        open={props.isDialogOpen}
        onClose={() => handlePaymentMethodClose()}
        title="Payment Method"
        size="md"
        busy={isLoading}
        // Card entry is hosted by Stripe Elements; a stray dismiss mid-tokenise
        // would leave the payment intent dangling.
        disableBackdropClose
        disableEscapeClose
        primaryLabel="Save"
        onPrimary={handleSubmit}
        primaryLoading={isLoading}
        primaryDisabled={isLoading || !isCardComplete}
        cancelLabel="Cancel"
        onCancel={() => handlePaymentMethodClose()}
      >
        {getBody()}
      </AppModal>
      <CustomLoader isLoading={isLoading} />
    </>
  );
};

export default AddPaymentMethodDialog;
